/**
 * EshopScanReport.jsx
 * ------------------------------------------------------------------
 * Renders one persisted eshop SEO scan report — top-line scorecard,
 * grade distribution, top issues, and a per-URL drill-down table with
 * filters + per-row "✨ AI Fix" buttons.
 *
 * Mounted in two places:
 *   1. Inline result on `/eshop-scanner` (after the user runs a fresh
 *      scan).
 *   2. (Future) Standalone share/read-only view if we expose a
 *      shareable report URL later.
 *
 * Filters are kept in component state — no URL-sync needed because the
 * report itself is canonical (you bookmark `/eshop-scanner?report=...`,
 * not the filter combo).
 */
import { useMemo, useState } from "react";
import GenerateFixModal from "./GenerateFixModal";
import {
  TrendingUp, TrendingDown, ShoppingCart, Folder, Newspaper, FileText,
  Sparkles, ExternalLink, AlertTriangle, ChevronDown,
} from "lucide-react";

const GRADE_COLOURS = {
  A: { bg: "bg-emerald-900/30", text: "text-emerald-300", border: "border-emerald-700/40", bar: "bg-emerald-500" },
  B: { bg: "bg-lime-900/30",    text: "text-lime-300",    border: "border-lime-700/40",    bar: "bg-lime-500" },
  C: { bg: "bg-amber-900/30",   text: "text-amber-300",   border: "border-amber-700/40",   bar: "bg-amber-500" },
  D: { bg: "bg-orange-900/30",  text: "text-orange-300",  border: "border-orange-700/40",  bar: "bg-orange-500" },
  F: { bg: "bg-rose-900/30",    text: "text-rose-300",    border: "border-rose-700/40",    bar: "bg-rose-500" },
};

const TYPE_ICONS = {
  product:  ShoppingCart,
  category: Folder,
  blog:     Newspaper,
  page:     FileText,
};

const TYPE_LABELS = {
  product:  "Produkt",
  category: "Kategória",
  blog:     "Blog",
  page:     "Stránka",
};

export default function EshopScanReport({ report, userEmail }) {
  const [filterType, setFilterType] = useState("all");
  const [filterGrade, setFilterGrade] = useState("all");
  const [fixModalRow, setFixModalRow] = useState(null);
  const [fixModalIssue, setFixModalIssue] = useState("");

  const results = report?.results || [];
  const summary = report?.summary || {};
  const score = Number(summary.score ?? 0);

  const filtered = useMemo(() => {
    return results.filter(r => {
      if (filterType !== "all" && (r.type || "page") !== filterType) return false;
      if (filterGrade !== "all" && (r.grade || "F") !== filterGrade) return false;
      return true;
    });
  }, [results, filterType, filterGrade]);

  // Headline arrow + colour based on the score so the top card reads
  // at-a-glance ("75 ✓" green, "32 ⚠" red).
  const scoreColour = score >= 80 ? "text-emerald-300"
                    : score >= 60 ? "text-lime-300"
                    : score >= 40 ? "text-amber-300"
                    : "text-rose-300";

  return (
    <div className="space-y-6" data-testid="eshop-scan-report">
      {/* Top-line scorecard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 p-6 bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Celkové skóre
          </div>
          <div className={`text-6xl font-black tabular-nums ${scoreColour}`} data-testid="eshop-report-score">
            {score}
            <span className="text-2xl text-gray-600 ml-1">/100</span>
          </div>
          <div className="mt-4 text-xs text-gray-400" data-testid="eshop-report-scanned">
            Scanovaných URL: <span className="text-white font-bold">{summary.scanned || 0}</span>
            <span className="mx-2 text-gray-700">·</span>
            <a href={report.domain} target="_blank" rel="noreferrer" className="text-purple-400 hover:underline">
              {report.domain} <ExternalLink className="inline w-3 h-3 ml-0.5" />
            </a>
          </div>
        </div>

        {/* Grade distribution */}
        <div className="lg:col-span-2 p-6 bg-gray-900/40 border border-gray-800 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-3">
            Rozdelenie známok (A → F)
          </div>
          <div className="grid grid-cols-5 gap-3" data-testid="eshop-report-grade-dist">
            {Object.keys(GRADE_COLOURS).map(g => {
              const count = (summary.grade_counts || {})[g] || 0;
              const pct = summary.scanned ? (count / summary.scanned) * 100 : 0;
              const c = GRADE_COLOURS[g];
              return (
                <div key={g} className={`p-3 ${c.bg} ${c.border} border rounded-sm`}>
                  <div className={`text-2xl font-black ${c.text}`}>{g}</div>
                  <div className="text-xs text-gray-400 mt-1">{count} URL</div>
                  <div className="mt-2 h-1 bg-gray-900 rounded-full overflow-hidden">
                    <div className={`h-full ${c.bar}`} style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Page type breakdown — secondary signal */}
          {Object.keys(summary.type_counts || {}).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-800 flex flex-wrap gap-2 text-xs">
              {Object.entries(summary.type_counts).map(([t, n]) => {
                const Icon = TYPE_ICONS[t] || FileText;
                return (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-900 border border-gray-800 rounded-sm text-gray-300">
                    <Icon className="w-3 h-3 text-gray-500" />
                    {TYPE_LABELS[t] || t}: <span className="text-white font-bold">{n}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Top 10 frequency issues */}
      {(summary.top_issues || []).length > 0 && (
        <div className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="eshop-report-top-issues">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <div className="text-sm font-bold">Top issues — fix tieto naprv (každý sa replikuje na desiatky URL)</div>
          </div>
          <ol className="space-y-2 text-sm">
            {summary.top_issues.map((it, idx) => (
              <li key={idx} className="flex items-center gap-3 p-2 bg-black/30 border border-gray-800 rounded-sm">
                <span className="text-purple-400 font-mono w-5 text-right">{idx + 1}.</span>
                <span className="flex-1 text-gray-300">{it.issue}</span>
                <span className="px-2 py-0.5 text-xs bg-purple-900/30 text-purple-300 border border-purple-700/40 rounded-sm tabular-nums">
                  {it.count}× URL
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Per-URL drill down + filters */}
      <div className="bg-gray-900/40 border border-gray-800 rounded-sm overflow-hidden">
        <header className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-800">
          <div className="text-sm font-bold">Per-URL detaily ({filtered.length})</div>
          <div className="flex-1" />
          <FilterDropdown
            label="Typ"
            value={filterType}
            onChange={setFilterType}
            testId="eshop-filter-type"
            options={[
              { value: "all", label: "Všetky typy" },
              { value: "product", label: "Produkty" },
              { value: "category", label: "Kategórie" },
              { value: "blog", label: "Blog" },
              { value: "page", label: "Stránky" },
            ]}
          />
          <FilterDropdown
            label="Známka"
            value={filterGrade}
            onChange={setFilterGrade}
            testId="eshop-filter-grade"
            options={[
              { value: "all", label: "Všetky známky" },
              { value: "A", label: "A — bez chýb" },
              { value: "B", label: "B" },
              { value: "C", label: "C" },
              { value: "D", label: "D" },
              { value: "F", label: "F — kritické" },
            ]}
          />
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="eshop-report-table">
            <thead className="bg-black/50 text-gray-500 uppercase tracking-widest">
              <tr>
                <th className="px-3 py-2 text-left">URL</th>
                <th className="px-3 py-2 text-left">Typ</th>
                <th className="px-3 py-2 text-center">Známka</th>
                <th className="px-3 py-2 text-center">Issues</th>
                <th className="px-3 py-2 text-left">Top problém</th>
                <th className="px-3 py-2 text-right">Fix</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">
                  Žiadne URL nezodpovedajú filtru.
                </td></tr>
              )}
              {filtered.map((row, i) => {
                const g = row.grade || "F";
                const c = GRADE_COLOURS[g] || GRADE_COLOURS.F;
                const Icon = TYPE_ICONS[row.type || "page"] || FileText;
                const topIssue = (row.issues || [])[0] || "—";
                return (
                  <tr
                    key={row.url + i}
                    className="border-t border-gray-900 hover:bg-black/30"
                    data-testid={`eshop-report-row-${i}`}
                  >
                    <td className="px-3 py-2 max-w-[280px]">
                      <a
                        href={row.url} target="_blank" rel="noreferrer"
                        className="text-purple-400 hover:underline break-all"
                        title={row.url}
                      >
                        {row.url.replace(/^https?:\/\//, "").substring(0, 60)}
                        {row.url.length > 60 ? "…" : ""}
                      </a>
                    </td>
                    <td className="px-3 py-2 text-gray-400">
                      <span className="inline-flex items-center gap-1">
                        <Icon className="w-3 h-3" />
                        {TYPE_LABELS[row.type || "page"] || row.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block w-7 text-center font-black ${c.bg} ${c.text} ${c.border} border rounded-sm py-0.5`}>
                        {g}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums">
                      {row.issues_count ?? (row.issues || []).length}
                    </td>
                    <td className="px-3 py-2 text-gray-400 max-w-[260px]">
                      <span title={topIssue} className="line-clamp-2">{topIssue}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {(row.issues || []).length > 0 ? (
                        <button
                          onClick={() => { setFixModalRow(row); setFixModalIssue(topIssue); }}
                          data-testid={`eshop-row-fix-btn-${i}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-600/80 hover:bg-purple-500 text-white rounded-sm"
                        >
                          <Sparkles className="w-3 h-3" />
                          AI Fix
                        </button>
                      ) : (
                        <span className="text-emerald-400 text-xs">✓ OK</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <GenerateFixModal
        open={!!fixModalRow}
        onClose={() => { setFixModalRow(null); setFixModalIssue(""); }}
        reportId={report.id}
        row={fixModalRow || {}}
        issue={fixModalIssue}
        userEmail={userEmail}
      />
    </div>
  );
}

function FilterDropdown({ label, value, onChange, options, testId }) {
  return (
    <label className="relative inline-flex items-center gap-2 text-xs">
      <span className="text-gray-500 uppercase tracking-widest">{label}</span>
      <span className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="appearance-none pl-3 pr-8 py-1.5 bg-black/50 border border-gray-800 text-gray-200 rounded-sm cursor-pointer focus:outline-none focus:border-purple-500"
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
      </span>
    </label>
  );
}
